import { HtmlHTMLAttributes, memo, MouseEventHandler, PointerEventHandler, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { animated, config, useTransition } from "react-spring";
import { Tooltip, TooltipInstance, TooltipProps } from "../Tooltip";
import { CopyIcon, CopySuccessIcon } from "./icons";

import styles from "./styles.module.scss";

export type CopyTextProps = {
  text: string
  tooltipProps?: Omit<TooltipProps, "children" | "content">
  iconClassName?: string
  copyTooltipText?: string
  copySuccessTooltipText?: string
} & HtmlHTMLAttributes<HTMLSpanElement>

export const CopyText = memo<CopyTextProps>((
  {
    text,
    children,
    className,
    tooltipProps,
    iconClassName,
    copyTooltipText = "Copy",
    copySuccessTooltipText = "Copied!",
    ...restProps
  }) => {
  const [ copied, setCopied ] = useState<boolean>(false);
  const [ hovered, setHovered ] = useState<boolean>(false);
  const [ isTooltipOpen, setIsTooltipOpen ] = useState<boolean>(false);
  const tooltip = useRef<TooltipInstance>(null);

  const transitions = useTransition(hovered, {
    from: { x: -25, opacity: 0, width: 0 },
    enter: { x: 3, opacity: 1, width: 25 },
    leave: { x: -25, opacity: 0, width: 0 },
    onChange: () => tooltip?.current?.update?.(),
    config: config.stiff,
  });

  const onCopy: MouseEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      setCopied(true);
      navigator?.clipboard?.writeText?.(text);
    }, [ text ]);
  const wrappedOnDoubleClick: PointerEventHandler<HTMLSpanElement> = useCallback((e) => {
    if (!e.isPropagationStopped()) {
      setIsTooltipOpen(true);
      setTimeout(() => setIsTooltipOpen(false), 100);
      onCopy(e);
      restProps?.onDoubleClick?.(e);
    }
  }, [ restProps, onCopy ]);
  const onPointerEnter: PointerEventHandler<HTMLSpanElement> = useCallback((e) => {
    setHovered(true);
    restProps?.onPointerEnter?.(e);
  }, [ setHovered, restProps ]);
  const onPointerLeave: PointerEventHandler<HTMLSpanElement> = useCallback((e) => {
    setHovered(false);
    setTimeout(() => setCopied(false), 100);
    restProps?.onPointerLeave?.(e);
  }, [ setHovered, restProps ]);

  return (
    <span
      onDoubleClick={wrappedOnDoubleClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={classNames(styles.root, className)}
      {...restProps}
    >
      {children ?? text}
      {transitions((style, hovered) => (
        hovered &&
        <Tooltip
          ref={tooltip}
          content={copied ? copySuccessTooltipText : copyTooltipText}
          delay={0}
          placement={"bottom"}
          isOpen={isTooltipOpen || copied}
          {...tooltipProps}
        >
          <animated.button
            style={style}
            type="button"
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerEnter={(e) => {
              e.preventDefault();
              setIsTooltipOpen(true);
            }}
            onPointerLeave={(e) => {
              e.preventDefault();
              setIsTooltipOpen(false);
            }}
            onClick={onCopy}
            className={classNames(styles.icon, iconClassName)}
          >
            {copied ? <CopySuccessIcon /> : <CopyIcon />}
          </animated.button>
        </Tooltip>
      ))}
    </span>
  );
});